import React from 'react';
import { BookOpenIcon, CalendarIcon, CheckCircleIcon, ClockIcon, MonitorIcon, UserIcon, UsersIcon } from 'lucide-react';
import { WebinarLink } from './WebinarLink';
export const WebinarDetails = ({
  openModal
}) => {
  const replaceWebinarWithLink = text => {
    const parts = text.split(/(\bwebinar\b)/i);
    return parts.map((part, index) => {
      if (part.toLowerCase() === 'webinar') {
        return <WebinarLink key={index} onClick={openModal} data-id="element-395">
            webinar
          </WebinarLink>;
      }
      return part;
    });
  };
  return <section className="mb-10" data-id="element-396">
      <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-397">
        Het webinar "Communiceren met je hond"
      </h3>
      <p className="mb-6 leading-relaxed" data-id="element-398">
        Dit is een online presentatie van 90 minuten. Gegeven door Amee. Je kunt
        het volgen vanaf je telefoon, tablet of computer. Het gaat over hoe je
        zonder straffen toch met je hond goed kunt samenwerken. Probleemgedrag
        leer je voorkomen in plaats van afstraffen. Om dit te volgen hoef je
        nergens naartoe te reizen - je volgt het gewoon thuis, op kantoor of
        onderweg via een internetverbinding. Tijdens het webinar kun je via de
        chat vragen stellen aan Amee, en na afloop ontvang je automatisch de
        opname zodat je alles terug kunt kijken wanneer het jou uitkomt (
        <a href="https://stresslessdogs.nl/mcc-gpm-adv1" className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" onClick={openModal} data-id="element-399">
          klik hier om ook mee te doen
        </a>
        ).
      </p>
      <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-400">
        Waarom 121.419 hondenbaasjes dit webinar al keken
      </h3>
      <p className="mb-6 leading-relaxed" data-id="element-401">
        Traditionele trainingsmethoden focussen op het veranderen van
        hondengedrag. Maar Amee's benadering is fundamenteel anders: in plaats
        van je hond te veranderen, leer je zijn taal begrijpen.
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-402">
        "Het is als het leren van gebarentaal," legt ze uit. "Je leert hoe
        honden onderling elkaar subtiele signalen geven met hun lichaam. Aan
        deze signalen kan je zien wat ze voelen en willen. Ook leer je hoe je
        als mens deze signalen kan gebruiken om jouw hond iets te vertellen in
        zijn taal."
      </p>
      <div className="bg-blue-50 p-5 rounded-lg mb-6 border-l-4 border-blue-500" data-id="element-403">
        <ul className="space-y-3" data-id="element-404">
          <li className="flex items-start" data-id="element-405">
            <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-406" />
            <span data-id="element-407">
              De vijf basis-signalen die elke hond gebruikt en wat ze betekenen
            </span>
          </li>
          <li className="flex items-start" data-id="element-408">
            <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-409" />
            <span data-id="element-410">
              Hoe je stress herkent voordat het escaleert naar probleemgedrag
            </span>
          </li>
          <li className="flex items-start" data-id="element-411">
            <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-412" />
            <span data-id="element-413">
              De drie grootste misverstanden over honden die 92% van de baasjes
              nog hebben
            </span>
          </li>
          <li className="flex items-start" data-id="element-414">
            <CheckCircleIcon className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-415" />
            <span data-id="element-416">
              Direct toepasbare technieken om terug te communiceren in zijn taal
            </span>
          </li>
        </ul>
      </div>
      <p className="mb-6 leading-relaxed" data-id="element-417">
        Maar het echte geheim zit in de volgorde waarin Amee deze kennis deelt.
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-418">
        "Ik begin met de signalen die mensen het meest herkennen, maar verkeerd
        interpreteren," legt ze uit. "Zodra ze doorhebben dat veel van het
        dominante gedrag eigenlijk heel iets anders betekent, valt alles op zijn
        plaats. Dan zijn de overige signalen logische uitbreidingen."
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-419">
        "Veel mensen vragen zich af of ze online wel iets over hun hond kunnen
        leren," grapt Amee. "Maar omdat hondentaal voor alle honden hetzelfde is
        kan ik je met voorbeeld video's en een goede presentatie al super veel
        leren over je eigen hond."
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-420">
        "Honden zijn geen mysterieuze wezens," zegt Amee. "Hondentaal is
        subtiel, maar glashelder. Je moet alleen leren waar je naar moet kijken.
        Daar help ik mensen mee via{' '}
        <a href="https://stresslessdogs.nl/mcc-gpm-adv1" className="text-blue-600 cursor-pointer hover:underline" target="_blank" rel="noopener noreferrer" onClick={openModal} data-id="element-421">
          dit webinar
        </a>
        "
      </p>
      <h3 className="text-lg font-bold text-blue-800 mb-4" data-id="element-422">
        Revolutie onder hondenbaasjes
      </h3>
      <div className="space-y-6 mb-6" data-id="element-423">
        <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-424">
          <p className="italic mb-2" data-id="element-425">
            "Na 20 minuten begreep ik al waarom onze wandelingen altijd
            chaotisch waren. Luna probeerde me de hele tijd te vertellen dat ze
            zich onveilig voelde, maar ik interpreteerde het als koppigheid. Het
            moment dat ik dat doorhad, veranderde alles."
          </p>
          <p className="text-right text-sm font-medium" data-id="element-426">
            - Lenneke uit Utrecht
          </p>
        </div>
        <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-427">
          <p className="italic mb-2" data-id="element-428">
            "Mijn vrouw en ik keken elkaar halverwege het webinar aan. 'Daarom
            doet hij dat!' zeiden we tegelijk. Plotseling maakten alle rare
            gewoontes van Rex perfect zin."
          </p>
          <p className="text-right text-sm font-medium" data-id="element-429">
            - Thomas uit Antwerpen
          </p>
        </div>
        <div className="bg-gray-50 p-5 rounded-lg border-l-4 border-blue-300" data-id="element-430">
          <p className="italic mb-2" data-id="element-431">
            "Het was emotioneel. Ik realiseerde me dat Buddy mij al maanden
            probeerde iets te vertellen over zijn angst, en ik dacht maar dat
            hij ongehoorzaam was. Ik voelde me schuldig, maar ook opgelucht.
            Eindelijk begreep ik hem."
          </p>
          <p className="text-right text-sm font-medium" data-id="element-432">
            - Sandra uit Amsterdam
          </p>
        </div>
      </div>
      <div className="bg-blue-50 p-6 rounded-lg mb-8 border border-blue-200" data-id="element-433">
        <div className="flex items-center mb-4" data-id="element-434">
          <CalendarIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-435" />
          <h3 className="font-bold" data-id="element-436">Webinar informatie</h3>
        </div>
        <p className="mb-4 leading-relaxed font-bold" data-id="element-437">
          Gratis webinar "Communiceren met je hond"
        </p>
        <ul className="mb-4 space-y-2" data-id="element-438">
          <li data-id="element-439">
            <strong data-id="element-440">Wanneer:</strong> Vandaag en morgen
          </li>
          <li data-id="element-441">
            <strong data-id="element-442">Tijden:</strong> 10:00, 13:00 en 20:00
          </li>
          <li data-id="element-443">
            <strong data-id="element-444">Waar:</strong> Online (thuis volgen)
          </li>
          <li data-id="element-445">
            <strong data-id="element-446">Kosten:</strong> Helemaal gratis
          </li>
        </ul>
        <p className="mb-4 leading-relaxed" data-id="element-447">
          Je kunt het webinar volgen op je telefoon, tablet of computer. Je
          hoeft nergens naartoe.
        </p>
        <div className="bg-white p-4 rounded-lg border border-blue-200 mb-5" data-id="element-448">
          <p className="font-bold text-blue-800 mb-2" data-id="element-449">Tijd dringt</p>
          <p className="text-blue-800" data-id="element-450">
            Het onderzoek is duidelijk: straffen is gevaarlijk. Elke dag dat je
            ermee doorgaat, wordt het erger.
          </p>
        </div>
        <a href="https://stresslessdogs.nl/mcc-gpm-adv1" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 block text-center" target="_blank" rel="noopener noreferrer" data-id="element-451">
          JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
        </a>
      </div>
      <h2 className="text-xl font-bold text-blue-800 mb-4 flex items-center" data-id="element-452">
        <MonitorIcon className="w-6 h-6 mr-2 text-blue-600" data-id="element-453" />
        Wat Amee tijdens het webinar vertelt
      </h2>
      <div className="space-y-4 mb-8" data-id="element-454">
        <div className="flex" data-id="element-455">
          <div className="bg-blue-600 text-white rounded-full min-w-8 h-8 flex items-center justify-center font-bold mr-3" data-id="element-456">
            1
          </div>
          <div data-id="element-457">
            <h4 className="font-bold" data-id="element-458">
              Minuut 1-20: Waarom straffen gevaarlijk is
            </h4>
            <p className="text-sm text-gray-700" data-id="element-459">
              Amee legt uit wat het onderzoek van 4.000 honden heeft laten zien.
              Waarom straffen honden agressief maakt. En waarom veel mensen dit
              niet weten.
            </p>
          </div>
        </div>
        <div className="flex" data-id="element-460">
          <div className="bg-blue-600 text-white rounded-full min-w-8 h-8 flex items-center justify-center font-bold mr-3" data-id="element-461">
            2
          </div>
          <div data-id="element-462">
            <h4 className="font-bold" data-id="element-463">
              Minuut 21-35: Hoe honden echt communiceren
            </h4>
            <p className="text-sm text-gray-700" data-id="element-464">
              Je leert hoe honden met elkaar praten. Welke signalen ze
              gebruiken. En waarom dit anders is dan wat veel mensen denken.
            </p>
          </div>
        </div>
        <div className="flex" data-id="element-465">
          <div className="bg-blue-600 text-white rounded-full min-w-8 h-8 flex items-center justify-center font-bold mr-3" data-id="element-466">
            3
          </div>
          <div data-id="element-467">
            <h4 className="font-bold" data-id="element-468">
              Minuut 36-55: De signalen van je hond begrijpen
            </h4>
            <p className="text-sm text-gray-700" data-id="element-469">
              Je leert de belangrijkste tekens die honden geven. Wat ze
              betekenen. En hoe je kunt zien wat je hond echt wil zeggen.
            </p>
          </div>
        </div>
        <div className="flex" data-id="element-470">
          <div className="bg-blue-600 text-white rounded-full min-w-8 h-8 flex items-center justify-center font-bold mr-3" data-id="element-471">
            4
          </div>
          <div data-id="element-472">
            <h4 className="font-bold" data-id="element-473">
              Minuut 56-75: Terug praten in hondentaal
            </h4>
            <p className="text-sm text-gray-700" data-id="element-474">
              Hoe je je hond kunt vertellen wat je wilt. Op een manier die hij
              begrijpt. Zonder straffen of schreeuwen.
            </p>
          </div>
        </div>
        <div className="flex" data-id="element-475">
          <div className="bg-blue-600 text-white rounded-full min-w-8 h-8 flex items-center justify-center font-bold mr-3" data-id="element-476">
            5
          </div>
          <div data-id="element-477">
            <h4 className="font-bold" data-id="element-478">
              Minuut 76-90: Jouw vragen beantwoorden
            </h4>
            <p className="text-sm text-gray-700" data-id="element-479">
              Amee beantwoordt vragen van mensen die meedoen. Ook vertelt ze
              over een speciale verrassing.
            </p>
          </div>
        </div>
      </div>
      <p className="mb-6 leading-relaxed" data-id="element-480">
        "Rond minuut 20 begrijpen mensen het," vertelt Amee. "Dan zie ik in de
        chat dat het kwartje valt. Ze snappen waarom hun oude manier van doen
        niet werkte."
      </p>
      <p className="mb-6 leading-relaxed" data-id="element-481">
        <a href="https://stresslessdogs.nl/mcc-gpm-adv1" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer" data-id="element-482">
          Klik hier om jezelf aan te melden voor het webinar.
        </a>
      </p>
      <div className="bg-blue-50 p-6 rounded-lg mb-8 border border-blue-200" data-id="element-483">
        <div className="flex items-center mb-4" data-id="element-484">
          <CalendarIcon className="w-5 h-5 text-blue-600 mr-2" data-id="element-485" />
          <h3 className="font-bold" data-id="element-486">Dit webinar kan snel volgeboekt zijn</h3>
        </div>
        <p className="mb-4 leading-relaxed" data-id="element-487">
          Ik moet eerlijk tegen je zijn - de vraag naar deze informatie is
          gewoon enorm.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-488">
          Sinds de resultaten van dit onderzoek meer bekend worden, melden zich
          elke week meer baasjes aan die eindelijk de waarheid willen weten.
        </p>
        <p className="mb-4 leading-relaxed" data-id="element-489">
          Het probleem is dat elk webinar maximaal 75 plekken heeft. En als alle
          plekken vol zijn? Dan kan ik pas volgende maand nieuwe sessies
          organiseren.
        </p>
        <div className="bg-white p-4 rounded-lg border border-blue-200 mb-5" data-id="element-490">
          <p className="font-bold text-blue-800 mb-2" data-id="element-491">
            Dit kan je enige kans zijn:
          </p>
          <p className="text-blue-800" data-id="element-492">
            Het organiseren van deze gratis webinars kost me enorm veel tijd en
            energie. Ik kan niet garanderen wanneer ik weer een reeks volgende
            gratis sessies doe.
          </p>
        </div>
        <a href="https://stresslessdogs.nl/mcc-gpm-adv1" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 block text-center" target="_blank" rel="noopener noreferrer" data-id="element-493">
          JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
        </a>
      </div>
      <div className="bg-green-50 p-6 rounded-lg border border-green-200 mb-8" data-id="element-494">
        <h3 className="text-lg font-bold text-green-800 mb-4" data-id="element-495">
          Over het populaire webinar van Amee: "Communiceren met je hond"
        </h3>
        <ul className="space-y-3 mb-5" data-id="element-496">
          <li className="flex items-start" data-id="element-497">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-498" />
            <span data-id="element-499">Volledig gratis - geen verborgen kosten</span>
          </li>
          <li className="flex items-start" data-id="element-500">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-501" />
            <span data-id="element-502">Te volgen vanaf telefoon, tablet of computer</span>
          </li>
          <li className="flex items-start" data-id="element-503">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-504" />
            <span data-id="element-505">Opname wordt toegestuurd</span>
          </li>
          <li className="flex items-start" data-id="element-506">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-507" />
            <span data-id="element-508">Maximaal 75 plekken per sessie</span>
          </li>
          <li className="flex items-start" data-id="element-509">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-510" />
            <span data-id="element-511">
              Speciale verrassingsbonus tijdens het{' '}
              {replaceWebinarWithLink('webinar')}
            </span>
          </li>
        </ul>
        <p className="font-bold mb-5" data-id="element-512">Hoe krijg je toegang:</p>
        <ol className="list-decimal pl-8 mb-5 space-y-2" data-id="element-513">
          <li data-id="element-514">Klik de aanmeldbutton hieronder</li>
          <li data-id="element-515">Kies je tijdslot (vandaag of morgen)</li>
          <li data-id="element-516">Vul het korte aanmeldformulier in</li>
          <li data-id="element-517">Ontvang direct je toegangslink via email</li>
          <li data-id="element-518">
            Klik 5 minuten voor de start op de link in mail (het webinar start
            dan automatisch. Je hebt geen software nodig.)
          </li>
        </ol>
        <a href="https://stresslessdogs.nl/mcc-gpm-adv1" className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 block text-center" target="_blank" rel="noopener noreferrer" data-id="element-519">
          JA, IK WIL DE WAARHEID WETEN - GRATIS AANMELDEN
        </a>
      </div>
    </section>;
};